import { useAppStore } from '@store'
import type { Scene, WebGLRenderer } from 'three'
import { Vector2 } from 'three'

const RING_BUFFER_SIZE = 300
const SNAPSHOT_INTERVAL_MS = 1000
const STORAGE_KEY = 'webgl_incident_last'
const LOG_PREFIX = '[WebGLDiag]'

export type WebGLDiagEventType =
  | 'snapshot'
  | 'contextlost'
  | 'contextrestored'
  | 'mode_change'
  | 'glb_clone'
  | 'init'
  | 'dispose'

export type WebGLSnapshot = {
  t: number
  event: WebGLDiagEventType
  mode: string
  frameloop: string
  performanceMode: boolean
  items: number
  sceneGLB: number
  meshes: number
  memory: { geometries: number; textures: number }
  render: { calls: number; triangles: number; points: number; lines: number }
  programs: number
  dpr: number
  canvasSize: [number, number]
  preserveDrawingBuffer: boolean
  shadowMaps: { count: number; maxSize: number }
  gpuMemAvailKB?: number
  gpuRenderer?: string
  activeGlbClones: number
  frameStats: {
    rendersLastFrame: number
    maxRendersPerFrame: number
    doubleRenderFrames: number
  }
  meta?: Record<string, unknown>
}

type GlbCloneAction = 'create' | 'destroy'

let enabled = false
let initialized = false
let renderer: WebGLRenderer | null = null
let sceneRef: Scene | null = null
let canvasEl: HTMLCanvasElement | null = null
let snapshotTimer: ReturnType<typeof setInterval> | null = null

const ringBuffer: WebGLSnapshot[] = []
let activeGlbClones = 0

let frameRenderCount = 0
let rendersLastFrame = 0
let maxRendersPerFrame = 0
let doubleRenderFrames = 0
let frameTrackerRaf = 0

let originalRender: WebGLRenderer['render'] | null = null

const textureCacheSizeProviders = new Set<() => number>()

export function isWebGLDiagnosticsEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem('webgl_diagnostics') === '1'
  } catch {
    return false
  }
}

export function registerTextureCacheSizeProvider(provider: () => number): () => void {
  textureCacheSizeProviders.add(provider)
  return () => textureCacheSizeProviders.delete(provider)
}

function getTextureCacheSize(): number {
  let total = 0
  textureCacheSizeProviders.forEach((provider) => {
    total += provider()
  })
  return total
}

function getGpuMemAvailKB(gl: WebGLRenderingContext | WebGL2RenderingContext): number | undefined {
  const ext = gl.getExtension('WEBGL_memory_info') as {
    GPU_MEMORY_INFO_CURRENT_AVAILABLE_VIDEOMEMORY_NVX?: number
  } | null
  if (!ext?.GPU_MEMORY_INFO_CURRENT_AVAILABLE_VIDEOMEMORY_NVX) return undefined
  return gl.getParameter(ext.GPU_MEMORY_INFO_CURRENT_AVAILABLE_VIDEOMEMORY_NVX) as number
}

function getGpuRenderer(gl: WebGLRenderingContext | WebGL2RenderingContext): string | undefined {
  const ext = gl.getExtension('WEBGL_debug_renderer_info') as {
    UNMASKED_RENDERER_WEBGL: number
  } | null
  if (!ext) return undefined
  return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string
}

function collectShadowMapStats(scene: Scene) {
  let count = 0
  let maxSize = 0
  scene.traverse((obj) => {
    const light = obj as { isLight?: boolean; castShadow?: boolean; shadow?: { mapSize?: { width: number; height: number } } }
    if (!light.isLight || !light.castShadow || !light.shadow) return
    count++
    const w = light.shadow.mapSize?.width ?? 0
    const h = light.shadow.mapSize?.height ?? 0
    maxSize = Math.max(maxSize, w, h)
  })
  return { count, maxSize }
}

function countMeshes(scene: Scene): number {
  let meshes = 0
  scene.traverse((obj) => {
    if ((obj as { isMesh?: boolean }).isMesh) meshes++
  })
  return meshes
}

function readStoreSnapshot() {
  const state = useAppStore.getState()
  return {
    mode: String(state.mode),
    frameloop: String(state.frameloop),
    performanceMode: Boolean(state.performanceMode),
    items: state.items?.size ?? 0,
    sceneGLB: state.sceneGLB?.size ?? 0,
  }
}

function buildSnapshot(
  event: WebGLDiagEventType,
  meta?: Record<string, unknown>,
): WebGLSnapshot | null {
  if (!renderer || !sceneRef) return null

  const store = readStoreSnapshot()
  const size = renderer.getSize(new Vector2())
  const glCtx = renderer.getContext()

  return {
    t: performance.now(),
    event,
    ...store,
    meshes: countMeshes(sceneRef),
    memory: {
      geometries: renderer.info.memory.geometries,
      textures: renderer.info.memory.textures,
    },
    render: {
      calls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      points: renderer.info.render.points,
      lines: renderer.info.render.lines,
    },
    programs: renderer.info.programs?.length ?? 0,
    dpr: renderer.getPixelRatio(),
    canvasSize: [size.width, size.height],
    preserveDrawingBuffer: Boolean(renderer.getContextAttributes()?.preserveDrawingBuffer),
    shadowMaps: collectShadowMapStats(sceneRef),
    gpuMemAvailKB: getGpuMemAvailKB(glCtx),
    gpuRenderer: getGpuRenderer(glCtx),
    activeGlbClones,
    frameStats: {
      rendersLastFrame,
      maxRendersPerFrame,
      doubleRenderFrames,
    },
    meta: {
      ...meta,
      textureCacheSize: getTextureCacheSize(),
    },
  }
}

function pushSnapshot(snapshot: WebGLSnapshot) {
  ringBuffer.push(snapshot)
  if (ringBuffer.length > RING_BUFFER_SIZE) {
    ringBuffer.shift()
  }
}

function countSceneGeometryBreakdown(scene: Scene) {
  let meshes = 0
  let withGeometry = 0
  let triangleMeshes = 0
  const byType = new Map<string, number>()

  scene.traverse((obj) => {
    const mesh = obj as { isMesh?: boolean; geometry?: { attributes?: { position?: { count: number } } } }
    if (!mesh.isMesh) return
    meshes++
    if (!mesh.geometry) return
    withGeometry++
    const pos = mesh.geometry.attributes?.position
    if (pos) triangleMeshes += Math.floor(pos.count / 3)
    const key = obj.type || 'Object3D'
    byType.set(key, (byType.get(key) ?? 0) + 1)
  })

  return { meshes, withGeometry, triangleMeshes, byType: Object.fromEntries(byType) }
}

function logSnapshot(event: WebGLDiagEventType, meta?: Record<string, unknown>) {
  const snapshot = buildSnapshot(event, meta)
  if (!snapshot) return
  pushSnapshot(snapshot)
  if (import.meta.env?.DEV && event === 'snapshot') {
    const geoPerMesh =
      snapshot.meshes > 0
        ? (snapshot.memory.geometries / snapshot.meshes).toFixed(1)
        : 'n/a'
    console.debug(
      `${LOG_PREFIX} snapshot`,
      `meshes=${snapshot.meshes}`,
      `geo=${snapshot.memory.geometries}`,
      `geo/mesh=${geoPerMesh}`,
      `tex=${snapshot.memory.textures}`,
      `calls=${snapshot.render.calls}`,
      `items=${snapshot.items}`,
      `clones=${snapshot.activeGlbClones}`,
      `shadowMax=${snapshot.shadowMaps.maxSize}`,
      `gpuKB=${snapshot.gpuMemAvailKB ?? 'n/a'}`,
    )
  }
}

function persistIncidentDump(reason: string) {
  try {
    const payload = {
      reason,
      dumpedAt: new Date().toISOString(),
      snapshots: ringBuffer,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    console.error(`${LOG_PREFIX} failed to persist incident dump`, error)
  }
}

export function analyzeSceneGeometry(): Record<string, unknown> | null {
  if (!sceneRef) return null
  const breakdown = countSceneGeometryBreakdown(sceneRef)
  const rendererGeometries = renderer?.info.memory.geometries ?? 0
  const report = {
    rendererGeometries,
    sceneMeshes: breakdown.meshes,
    geoToMeshRatio:
      breakdown.meshes > 0 ? Number((rendererGeometries / breakdown.meshes).toFixed(2)) : null,
    note:
      'renderer.info.memory.geometries es acumulativo (geometrías creadas sin dispose). Si geo/mesh >> 1, hay fugas o CSG recreando buffers.',
    ...breakdown,
  }
  console.info(`${LOG_PREFIX} scene geometry analysis`, report)
  return report
}

export function dumpWebGLDiagnostics(reason = 'manual'): WebGLSnapshot[] {
  logSnapshot('snapshot', { reason })
  const dump = [...ringBuffer]
  console.group(`${LOG_PREFIX} dump (${reason})`)
  console.table(
    dump.slice(-30).map((s) => ({
      t: Math.round(s.t),
      event: s.event,
      mode: s.mode,
      items: s.items,
      geo: s.memory.geometries,
      tex: s.memory.textures,
      calls: s.render.calls,
      clones: s.activeGlbClones,
      shadowMax: s.shadowMaps.maxSize,
      dblFrames: s.frameStats.doubleRenderFrames,
      gpuKB: s.gpuMemAvailKB ?? 'n/a',
    })),
  )
  console.log(`${LOG_PREFIX} full ring buffer`, dump)
  console.groupEnd()
  persistIncidentDump(reason)
  return dump
}

export function logWebGLEvent(event: WebGLDiagEventType, meta?: Record<string, unknown>) {
  if (!enabled) return
  logSnapshot(event, meta)
  if (event !== 'snapshot') {
    console.info(`${LOG_PREFIX} ${event}`, meta ?? {})
  }
}

export function logGlbClone(action: GlbCloneAction, meta: Record<string, unknown>) {
  if (!enabled) return
  if (action === 'create') activeGlbClones++
  else activeGlbClones = Math.max(0, activeGlbClones - 1)
  logSnapshot('glb_clone', { action, activeGlbClones, ...meta })
}

/** Registra un render manual (p.ej. AnimationControl) — el patch global de gl.render ya cuenta todo. */
export function trackManualRender(_gl: WebGLRenderer) {
  // Hook reservado por si hace falta metadata adicional en el futuro.
}

function startFrameRenderTracker() {
  const tick = () => {
    rendersLastFrame = frameRenderCount
    if (frameRenderCount > 1) doubleRenderFrames++
    if (frameRenderCount > maxRendersPerFrame) maxRendersPerFrame = frameRenderCount
    frameRenderCount = 0
    frameTrackerRaf = requestAnimationFrame(tick)
  }
  frameTrackerRaf = requestAnimationFrame(tick)
}

function stopFrameRenderTracker() {
  if (frameTrackerRaf) cancelAnimationFrame(frameTrackerRaf)
  frameTrackerRaf = 0
}

function patchRenderer(gl: WebGLRenderer) {
  if (originalRender) return
  originalRender = gl.render.bind(gl)
  gl.render = ((...args: Parameters<WebGLRenderer['render']>) => {
    frameRenderCount++
    return originalRender!(...args)
  }) as WebGLRenderer['render']
}

function unpatchRenderer(gl: WebGLRenderer) {
  if (originalRender) {
    gl.render = originalRender
    originalRender = null
  }
}

function onContextLost(event: Event) {
  event.preventDefault()
  const snapshot = buildSnapshot('contextlost', {
    statusMessage: (event as Event & { statusMessage?: string }).statusMessage,
  })
  if (snapshot) pushSnapshot(snapshot)
  console.error(`${LOG_PREFIX} webglcontextlost`, snapshot)
  dumpWebGLDiagnostics('webglcontextlost')
}

function onContextRestored() {
  logWebGLEvent('contextrestored')
  console.warn(`${LOG_PREFIX} webglcontextrestored — renderer may need full scene rebuild`)
}

export function initWebGLDiagnostics(gl: WebGLRenderer, scene: Scene) {
  if (!isWebGLDiagnosticsEnabled()) return
  if (initialized && renderer === gl) return

  enabled = true
  initialized = true
  renderer = gl
  sceneRef = scene
  canvasEl = gl.domElement

  patchRenderer(gl)
  startFrameRenderTracker()

  canvasEl.addEventListener('webglcontextlost', onContextLost)
  canvasEl.addEventListener('webglcontextrestored', onContextRestored)

  snapshotTimer = setInterval(() => logSnapshot('snapshot'), SNAPSHOT_INTERVAL_MS)

  logWebGLEvent('init', {
    gpuRenderer: getGpuRenderer(gl.getContext()),
    preserveDrawingBuffer: gl.getContextAttributes()?.preserveDrawingBuffer,
  })

  if (typeof window !== 'undefined') {
    ;(window as Window & {
      __webglDiagnostics?: {
        dump: typeof dumpWebGLDiagnostics
        analyzeScene: typeof analyzeSceneGeometry
      }
    }).__webglDiagnostics = {
      dump: dumpWebGLDiagnostics,
      analyzeScene: analyzeSceneGeometry,
    }
  }
}

export function disposeWebGLDiagnostics() {
  if (!initialized) return

  logWebGLEvent('canvas_cleanup', { pathname: window.location.pathname })

  if (snapshotTimer) clearInterval(snapshotTimer)
  snapshotTimer = null

  stopFrameRenderTracker()

  if (canvasEl) {
    canvasEl.removeEventListener('webglcontextlost', onContextLost)
    canvasEl.removeEventListener('webglcontextrestored', onContextRestored)
  }

  if (renderer) unpatchRenderer(renderer)

  enabled = false
  initialized = false
  renderer = null
  sceneRef = null
  canvasEl = null
}
